'use client';

import {
  createContext,
  useContext,
  PropsWithChildren,
  useState,
  useCallback,
} from 'react';

const LoadBoardViewedTabIdsContext = createContext<Set<string> | null>(null);
const LoadBoardMarkTabIdAsViewedContext = createContext<
  ((id: string) => void) | null
>(null);

type MarkTabAsViewedFunction = () => void;

export function useLoadBoardTabViewState(
  id: string,
): [boolean, MarkTabAsViewedFunction] {
  const viewedTabIds = useContext(LoadBoardViewedTabIdsContext);
  const markTabIdAsViewed = useContext(LoadBoardMarkTabIdAsViewedContext);

  if (viewedTabIds === null || markTabIdAsViewed === null) {
    throw new Error(
      'useLoadBoardTabViewState must be used within a LoadBoardTabsViewStateProvider',
    );
  }

  const markTabAsViewed = useCallback(
    () => markTabIdAsViewed(id),
    [id, markTabIdAsViewed],
  );

  return [viewedTabIds.has(id), markTabAsViewed];
}

export default function LoadBoardTabsViewStateProvider({
  children,
}: PropsWithChildren) {
  const [viewedTabIds, setViewedTabIds] = useState<Set<string>>(new Set());
  const markTabIdAsViewed = useCallback(
    (id: string) => setViewedTabIds((prev) => new Set(prev).add(id)),
    [],
  );

  return (
    <LoadBoardViewedTabIdsContext.Provider value={viewedTabIds}>
      <LoadBoardMarkTabIdAsViewedContext.Provider value={markTabIdAsViewed}>
        {children}
      </LoadBoardMarkTabIdAsViewedContext.Provider>
    </LoadBoardViewedTabIdsContext.Provider>
  );
}
